import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { ArrowLeft, Save, Trash2, Calendar } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function AddNote() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedDate = searchParams.get("date") || "";

  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [existingNoteId, setExistingNoteId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchNoteForDate();
    }
  }, [selectedDate]);

  const fetchNoteForDate = async () => {
    try {
      setIsFetching(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/notes/date/${selectedDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.note) {
        setContent(response.data.note.content);
        setExistingNoteId(response.data.note._id);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error("Error fetching note:", error);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      if (!content.trim()) {
        setError("Please enter some content for your note");
        return;
      }

      const token = localStorage.getItem("authToken");

      if (existingNoteId) {
        // Update existing note
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}api/notes/${existingNoteId}`,
          { content },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSuccess("Note updated successfully!");
      } else {
        // Create new note
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}api/notes`,
          {
            date: selectedDate,
            content,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setExistingNoteId(response.data.note._id);
        setSuccess("Note saved successfully!");
      }

      setTimeout(() => {
        navigate("/employee/notes");
      }, 1500);
    } catch (error: any) {
      console.error("Error saving note:", error);
      setError(error.response?.data?.message || "Failed to save note");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingNoteId) return;

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("authToken");

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}api/notes/${existingNoteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess("Note deleted successfully!");
      setTimeout(() => {
        navigate("/employee/notes");
      }, 1000);
    } catch (error: any) {
      console.error("Error deleting note:", error);
      setError(error.response?.data?.message || "Failed to delete note");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">Loading note...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/employee/notes")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {existingNoteId ? "Edit Note" : "Add Note"}
          </h2>
          <p className="text-muted-foreground flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4" />
            {formatDate(selectedDate)}
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 text-green-600 bg-green-50 border border-green-200 rounded-md">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Note Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="content">
                Your notes, tasks, or reminders for this day
              </Label>
              <Textarea
                id="content"
                placeholder="Write your notes here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={isLoading}
                rows={12}
                className="resize-none"
              />
              <p className="text-sm text-muted-foreground">
                {content.length} characters
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-between">
              <div>
                {existingNoteId && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isLoading || isDeleting}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Note
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/employee/notes")}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner variant="ellipsis" size={20} />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Note
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
